<script setup>
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { useWebSiteStore } from '@/stores/web-site.store'
import { promptCurrencyChange } from '@/composables/exchange-rate'
import CurrencyInput from '@/components/shared/CurrencyInput.vue'
import VatSelect from '@/components/shared/VatSelect.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  mobile: {
    type: Boolean,
    default: false,
  },
  fieldPrefix: {
    type: String,
    default: 'rc_',
  },
})

const emit = defineEmits(['update:modelValue'])

const i18n = getI18n()
const webSiteStore = useWebSiteStore()

const f = (name) => `${props.fieldPrefix}${name}`

const currentCurrency = computed(() => webSiteStore.currencies.find(i => i.id === props.modelValue[f('currency_ref')]) || webSiteStore.getDefaultCurrency())

const required = [val => !!val || i18n.t('common.required')]
const isReadonly = computed(() => !webSiteStore.can(webSiteStore.permissionFlagEnum.RATE_CARDS_FULL))

const updateField = (field, value) => {
  emit('update:modelValue', { ...props.modelValue, [f(field)]: value })
}

const onCurrencyChange = async(currencyRef) => {
  const amounts = await promptCurrencyChange(props.modelValue[f('currency_ref')], currencyRef, {
    [f('price')]: props.modelValue[f('price')],
    [f('cost')]: props.modelValue[f('cost')],
  })
  if (amounts) emit('update:modelValue', { ...props.modelValue, ...amounts, [f('currency_ref')]: currencyRef })
}
</script>

<template>
  <div class="row q-col-gutter-sm">
    <q-select
      :model-value="modelValue[f('currency_ref')]"
      :disable="isReadonly"
      :class="mobile ? 'col-4' : 'col-2'"
      :label="i18n.t('common.currency')"
      option-value="id"
      option-label="code"
      emit-value
      map-options
      :options="webSiteStore.currencies"
      :rules="required"
      @update:model-value="onCurrencyChange"
    />
    <currency-input
      :model-value="modelValue[f('price')]"
      :disable="isReadonly"
      :class="mobile ? 'col-4' : 'col-2'"
      :label="i18n.t('common.price')"
      :suffix="currentCurrency.symbol"
      @update:model-value="(value) => updateField('price', value)"
    />
    <currency-input
      :model-value="modelValue[f('cost')]"
      :disable="isReadonly"
      :class="mobile ? 'col-4' : 'col-2'"
      :label="i18n.t('common.cost')"
      :suffix="currentCurrency.symbol"
      @update:model-value="(value) => updateField('cost', value)"
    />
    <q-select
      :model-value="modelValue[f('unit_of_measure_ref')]"
      :disable="isReadonly"
      :class="mobile ? 'col-6' : 'col-3'"
      :label="i18n.t('common.unitOfMeasure')"
      option-value="id"
      option-label="name"
      :options="webSiteStore.getLocalizedUnitsOfMeasure()"
      :rules="required"
      @update:model-value="(val) => updateField('unit_of_measure_ref', val)"
    />
    <vat-select
      :model-value="modelValue[f('vat_type_ref')]"
      :disable="isReadonly"
      :class="mobile ? 'col-6' : 'col-3'"
      :rules="required"
      @update:model-value="(val) => updateField('vat_type_ref', val)"
    />
  </div>
</template>
